import { Box, Flex } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation } from 'react-router-dom'
import { AuthContext } from '../../hooks/auth/useAuth'
import { useCurrentSelf } from '../../hooks/auth/useCurrentSelf'
import { useRole } from '../../hooks/auth/useRole'
import { useRouteReadyMetric } from '../../lib/performance/monitoring'
import Loading from '../../pages/Loading'
import NavigationBar from '../Navbar'
import PageHeader from '../PageHeader'
import GlobalSearch from '../GlobalSearch'
import HRChatWidget from '../hr-chat/HRChatWidget'
import { myPermissionsQueryOptions } from '../../lib/performance/queryOptions'

const CONTENT_AREA_BG = 'linear-gradient(135deg, #EEEDF6 0%, #F5F4F8 40%, #F8F4F4 70%, #FBF0F0 100%)'
const MotionBox = motion(Box)

const UserWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const auth = useContext(AuthContext)
  const { t } = useTranslation()
  const { role, isAdmin, isSuperAdmin, isOrgAdmin, isFinance } = useRole()

  const { data: myPermissions, isLoading: isPermissionsLoading } = useQuery(
    myPermissionsQueryOptions(!isOrgAdmin && !isSuperAdmin),
  )

  const hasWorkforceAccess =
    isOrgAdmin || isSuperAdmin || (myPermissions?.workforce?.length ?? 0) > 0
  const hasRecruitmentAccess =
    isOrgAdmin || isSuperAdmin || (myPermissions?.recruitment?.length ?? 0) > 0
  const hasExploreAccess =
    isOrgAdmin || isSuperAdmin || (myPermissions?.explore?.length ?? 0) > 0
  const hasHRChatAccess =
    isOrgAdmin || isSuperAdmin || (myPermissions?.support?.includes('hr-chat') ?? false)

  const isWorkforceRoute = location.pathname.startsWith('/user/workforce')
  const isPayrollRoute = location.pathname.startsWith('/user/payroll')
  const isClassificationRoute = location.pathname.startsWith('/user/classification')
  const isDocumentRoute = location.pathname.startsWith('/user/admin/document')
  const isUserDocumentRoute = location.pathname.startsWith('/user/document') && !location.pathname.startsWith('/user/admin/document')
  const isOrganizationRoute = location.pathname.startsWith('/user/organization')
  const isExploreRoute = location.pathname.startsWith('/user/explore') || location.pathname.startsWith('/user/content')
  const isContentAuthoringRoute =
    location.pathname === '/user/content/new' || /^\/user\/content\/[^/]+\/edit$/.test(location.pathname)
  const isSettingsRoute = location.pathname.startsWith('/user/settings')
  const isPlanningRoute = location.pathname.startsWith('/user/planning')
  const isRecruitmentRoute = location.pathname.startsWith('/user/recruitment')
  const isOutflowRoute = location.pathname.startsWith('/user/outflow')
  const isFaqRoute = location.pathname.startsWith('/user/faq')
  const isHRChatRoute = location.pathname.startsWith('/user/hr-chat')
  const isPathsRoute = /^\/user\/explore\/paths\/[^/]+$/.test(location.pathname)
  const showChrome = !isPathsRoute
  const isPermissionGuardedRoute = isWorkforceRoute || isRecruitmentRoute || isExploreRoute || isHRChatRoute
  useRouteReadyMetric(location.pathname)

  const getTabConfig = () => {
    const filterTabsByPermissions = (
      section: 'planning' | 'workforce' | 'recruitment' | 'explore',
      tabs: { title: string; path: string; tab: string }[],
    ) => {
      if (isOrgAdmin || isSuperAdmin) return tabs
      if (isPermissionsLoading) return []

      const allowedTabs = myPermissions?.[section] || []
      if (section !== 'planning') {
        return tabs.filter((tab) => allowedTabs.includes(tab.tab))
      }

      const childToParent: Record<string, string> = {
        'weekly-schedule': 'attendance-calendar',
        'submit-request': 'attendance-calendar',
        'leave-requests': 'leave-management',
        'leave-balances': 'leave-management',
        'public-holidays': 'leave-management',
        'desks': 'workspace',
        'rooms': 'workspace',
        'offices': 'workspace',
        'projects': 'time-tracking',
        'time-entries': 'time-tracking',
        'activities': 'time-tracking',
      }
      const effectiveTabs = new Set(allowedTabs)
      for (const tab of allowedTabs) {
        const parent = childToParent[tab]
        if (parent) effectiveTabs.add(parent)
      }
      return tabs.filter((tab) => effectiveTabs.has(tab.tab))
    }

    if (isWorkforceRoute) {
      const canSeeMutations = role === 'country_admin' || isOrgAdmin || isSuperAdmin || isFinance
      const workforceTabs = [
        { title: t('workforce.tabs.people'), path: '/user/workforce/people', tab: 'people' },
        { title: t('workforce.tabs.teams'), path: '/user/workforce/teams', tab: 'teams' },
        { title: t('workforce.tabs.organization'), path: '/user/workforce/organization', tab: 'organization' },
        { title: t('workforce.tabs.assets'), path: '/user/workforce/assets', tab: 'assets' },
        ...(canSeeMutations ? [{ title: t('workforce.tabs.mutations'), path: '/user/workforce/mutations', tab: 'mutations' }] : []),
      ]
      return filterTabsByPermissions('workforce', workforceTabs)
    }
    if (isClassificationRoute) {
      return [
        { title: t('classification.tabs.jobProfile'), path: '/user/classification/jobprofile', tab: 'jobprofile' },
        { title: t('classification.tabs.skills'), path: '/user/classification/skills', tab: 'skills' },
        { title: t('classification.tabs.roles'), path: '/user/classification/roles', tab: 'roles' },
        { title: t('classification.tabs.accountabilityAreas'), path: '/user/classification/accountability', tab: 'accountability' },
      ]
    }
    if (isUserDocumentRoute) {
      return [
        { title: t('documents.tabs.myDocuments'), path: '/user/document/documents', tab: 'documents' },
        { title: t('documents.tabs.hrDocuments'), path: '/user/document/hrinfo', tab: 'hrinfo' },
      ]
    }
    if (isDocumentRoute) {
      const isOrgOrSuperAdmin = role === 'org_admin' || role === 'super_admin'
      return [
        { title: t('documents.tabs.documentFlow'), path: '/user/admin/document/documents', tab: 'documents' },
        { title: t('documents.tabs.templates'), path: '/user/admin/document/templates', tab: 'templates' },
        { title: t('documents.tabs.hrInfo'), path: '/user/admin/document/hrinfo', tab: 'hrinfo' },
        ...(isOrgOrSuperAdmin ? [{ title: t('documents.tabs.statistics'), path: '/user/admin/document/statistics', tab: 'statistics' }] : []),
      ]
    }
    if (isOrganizationRoute) {
      return [
        { title: t('organization.tabs.myTeams'), path: '/user/organization', tab: 'myTeams' },
        { title: t('organization.tabs.myOrganization'), path: '/user/organization/organization', tab: 'organization' },
      ]
    }
    if (isContentAuthoringRoute) {
      return null
    }
    if (isExploreRoute && showChrome) {
      const isOrgOrSuperAdmin = role === 'org_admin' || role === 'super_admin'
      const exploreTabs = [
        { title: t('explore.tabs.explore'), path: '/user/explore/content', tab: 'explore' },
        ...(isAdmin ? [{ title: t('explore.tabs.library'), path: '/user/explore/library', tab: 'library' }] : []),
        isAdmin
          ? { title: t('explore.tabs.paths'), path: '/user/explore/paths', tab: 'paths' }
          : { title: t('explore.tabs.paths'), path: '/user/explore/userpath', tab: 'paths' },
          ...(isOrgOrSuperAdmin ? [{ title: t('explore.tabs.statistics'), path: '/user/explore/statistics', tab: 'statistics' }] : []),
      ]
      return filterTabsByPermissions('explore', exploreTabs)
    }
    if (isPlanningRoute) {
      const allPlanningTabs = [
        { title: t('planning.tabs.calendar'), path: '/user/planning/attendance-calendar', tab: 'attendance-calendar' },
        { title: t('planning.tabs.pastRequests'), path: '/user/planning/past-requests', tab: 'past-requests' },
        { title: t('planning.tabs.attendance'), path: '/user/planning/attendance', tab: 'attendance' },
        { title: t('planning.tabs.leaveManagement'), path: '/user/planning/leave-management', tab: 'leave-management' },
        { title: t('planning.tabs.workspace'), path: '/user/planning/workspace', tab: 'workspace' },
        { title: t('planning.tabs.timeTracking'), path: '/user/planning/time-tracking', tab: 'time-tracking' },
      ]
      return filterTabsByPermissions('planning', allPlanningTabs)
    }
    if (isPayrollRoute) {
      return [
        { title: t('payroll.tabs.overview'), path: '/user/payroll/people', tab: 'overview' },
        { title: t('payroll.tabs.benefits'), path: '/user/payroll/benefits', tab: 'benefits' },
        ...((isOrgAdmin || isSuperAdmin) ? [{ title: t('payroll.tabs.benefitMutations'), path: '/user/payroll/benefit-mutations', tab: 'benefit-mutations' }] : []),
      ]
    }
    if (isRecruitmentRoute) {
      const recruitmentTabs = [
        { title: t('recruitment.tabs.candidates'), path: '/user/recruitment/candidates', tab: 'candidates' },
        { title: t('recruitment.tabs.pipeline'), path: '/user/recruitment/pipeline', tab: 'pipeline' },
        { title: t('recruitment.tabs.interviews'), path: '/user/recruitment/interviews', tab: 'interviews' },
        { title: t('recruitment.tabs.reports'), path: '/user/recruitment/reports', tab: 'reports' },
        { title: t('recruitment.tabs.vacancy'), path: '/user/recruitment/vacancy', tab: 'vacancy' },
        { title: t('recruitment.tabs.overview'), path: '/user/recruitment/overview', tab: 'overview' },
        { title: t('recruitment.tabs.inflow'), path: '/user/recruitment/inflow', tab: 'inflow' },
      ]
      return filterTabsByPermissions('recruitment', recruitmentTabs)
    }
    if (isOutflowRoute) {
      return [
        { title: t('outflow.tabs.exitManagement'), path: '/user/outflow/exit-management', tab: 'exit-management' },
        { title: t('outflow.tabs.exitInterviews'), path: '/user/outflow/exit-interviews', tab: 'exit-interviews' },
        { title: t('outflow.tabs.outstandingAssets'), path: '/user/outflow/outstanding-assets', tab: 'outstanding-assets' },
        { title: t('outflow.tabs.turnoverReports'), path: '/user/outflow/turnover-reports', tab: 'turnover-reports' },
      ]
    }
    if (isFaqRoute) {
      if (isOrgAdmin || isSuperAdmin) {
        return [
          { title: t('faq.tabs.browse'), path: '/user/faq', tab: 'browse' },
          { title: t('faq.tabs.manage'), path: '/user/faq/manage', tab: 'manage' },
        ]
      }
      return null
    }
    if (isSettingsRoute) {
      return [
        { title: t('settings.tabs.general'), path: '/user/settings/general', tab: 'general' },
        { title: t('settings.tabs.modules'), path: '/user/settings/modules', tab: 'modules' },
        { title: t('settings.tabs.payments'), path: '/user/settings/payments', tab: 'payments' },
        { title: t('settings.tabs.access'), path: '/user/settings/access', tab: 'access' },
        ...(isSuperAdmin ? [
          { title: t('settings.tabs.organizations'), path: '/user/settings/organizations', tab: 'organizations' },
          { title: t('settings.tabs.plans'), path: '/user/settings/plans', tab: 'plans' },
        ] : []),
      ]
    }
    return null
  }

  const tabConfig = getTabConfig()
  const visibleTabConfig = tabConfig && tabConfig.length > 1 ? tabConfig : null

  const getInitialTab = () => {
    if (isWorkforceRoute) {
      if (location.pathname.includes('teams')) return 'teams'
      if (location.pathname.includes('organization')) return 'organization'
      if (location.pathname.includes('assets')) return 'assets'
      if (location.pathname.includes('mutations')) return 'mutations'
      return 'people'
    }
    if (isClassificationRoute) {
      if (location.pathname.includes('skills')) return 'skills'
      if (location.pathname.includes('roles')) return 'roles'
      if (location.pathname.includes('accountability')) return 'accountability'
      return 'jobprofile'
    }
    if (isUserDocumentRoute) {
      if (location.pathname.includes('hrinfo')) return 'hrinfo'
      return 'documents'
    }
    if (isDocumentRoute) {
      if (location.pathname.includes('documents')) return 'documents'
      if (location.pathname.includes('templates')) return 'templates'
      if (location.pathname.includes('statistics')) return 'statistics'
      if (location.pathname.includes('hrdocuments')) return 'hrdocuments'
      return 'hrinfo'
    }
    if (isOrganizationRoute) {
      if (location.pathname.includes('/user/organization/organization')) return 'organization'
      return 'myTeams'
    }
    if (isExploreRoute) {
      if (location.pathname.includes('statistics')) return 'statistics'
      if (location.pathname.startsWith('/user/content/')) return isAdmin ? 'library' : 'explore'
      if (location.pathname.includes('content')) return 'explore'
      if (location.pathname.includes('library')) return 'library'
      if (location.pathname.includes('paths') || location.pathname.includes('userpath')) return 'paths'
    }
    if (isPayrollRoute) {
      if (location.pathname.includes('benefit-mutations')) return 'benefit-mutations'
      if (location.pathname.includes('benefits')) return 'benefits'
      return 'overview'
    }
    if (isRecruitmentRoute) {
      if (location.pathname.includes('pipeline')) return 'pipeline'
      if (location.pathname.includes('interviews')) return 'interviews'
      if (location.pathname.includes('reports')) return 'reports'
      if (location.pathname.includes('vacancy')) return 'vacancy'
      if (location.pathname.includes('overview')) return 'overview'
      if (location.pathname.includes('inflow')) return 'inflow'
      return 'candidates'
    }
    if (isOutflowRoute) {
      if (location.pathname.includes('exit-interviews')) return 'exit-interviews'
      if (location.pathname.includes('outstanding-assets')) return 'outstanding-assets'
      if (location.pathname.includes('turnover-reports')) return 'turnover-reports'
      return 'exit-management'
    }
    if (isFaqRoute) {
      if (location.pathname.includes('manage')) return 'manage'
      return 'browse'
    }
    if (isSettingsRoute) {
      if (location.pathname.includes('organizations')) return 'organizations'
      if (location.pathname.includes('plans')) return 'plans'
      if (location.pathname.includes('modules')) return 'modules'
      if (location.pathname.includes('payments')) return 'payments'
      if (location.pathname.includes('integration')) return 'integration'
      if (location.pathname.includes('access')) return 'access'
      if (location.pathname.includes('general')) return 'general'
      return 'general'
    }
    if (isPlanningRoute) {
      if (location.pathname.includes('attendance-calendar')) return 'attendance-calendar'
      if (location.pathname.includes('past-requests')) return 'past-requests'
      if (location.pathname.includes('leave-management')) return 'leave-management'
      if (location.pathname.includes('timeline')) return 'timeline'
      if (location.pathname.includes('attendance')) return 'attendance'
      if (location.pathname.includes('workspace')) return 'workspace'
      if (location.pathname.includes('time-tracking')) return 'time-tracking'
      return 'attendance-calendar'
    }
    return 'people'
  }

  const [selectedTab, setSelectedTab] = useState(getInitialTab)
  const [searchOpen, setSearchOpen] = useState(false)
  const openSearch = useCallback(() => setSearchOpen(true), [])
  const closeSearch = useCallback(() => setSearchOpen(false), [])

  // ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  // Use auth user data (already fetched by useAuth) instead of a separate query
  const user = auth?.user
  const { isLoading: isCurrentSelfLoading } = useCurrentSelf(!!user?.id)

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      localStorage.removeItem('selectedNav')
    }
  }, [auth?.isAuthenticated])

  useEffect(() => {
    setSelectedTab(getInitialTab)
  }, [location.pathname])

  // Auth guards
  if (!auth?.isAuthenticated) {
    return <Navigate to="/auth/login" replace />
  }
  const mustCompleteMFA = auth.requiresMFA || (auth.mfaRequiredForUser && !auth.mfaVerifiedThisSession)
  if (mustCompleteMFA) {
    return <Navigate to="/auth/mfa" replace />
  }
  if (isCurrentSelfLoading || auth?.loading?.query) {
    return <Loading />
  }
  if (auth.isVerified === false) {
    return <Navigate to="/auth/verification" replace />
  }

  if (isPermissionsLoading && !isOrgAdmin && !isSuperAdmin && isPermissionGuardedRoute) {
    return <Loading />
  }

  if (isWorkforceRoute && !hasWorkforceAccess) {
    return <Navigate to="/user/home" replace />
  }

  if (isRecruitmentRoute && !hasRecruitmentAccess) {
    return <Navigate to="/user/home" replace />
  }

  if (isExploreRoute && !hasExploreAccess) {
    return <Navigate to="/user/home" replace />
  }

  if (isHRChatRoute && !hasHRChatAccess) {
    return <Navigate to="/user/home" replace />
  }

  return (
    <Flex
      w="100vw"
      h={{ base: 'auto', md: '100vh' }}
      minH={{ base: '100vh', md: '100vh' }}
      bg="white"
      overflow={{ base: 'visible', md: 'hidden' }}
    >
      {/* Navbar — hidden on mobile via its own display prop */}
      {showChrome && <NavigationBar />}

      {/* Content area */}
      <Flex
        direction="column"
        flex="1"
        background={CONTENT_AREA_BG}
        borderTopLeftRadius="0"
        overflow={{ base: 'visible', md: 'hidden' }}
        w={{ base: '100%', md: showChrome ? 'calc(100% - 230px)' : '100%' }}
      >
        {/* PageHeader: title + subtitle + user menu + optional tabs */}
        {showChrome && (
          <PageHeader
            tabConfig={visibleTabConfig}
            selectedTab={selectedTab}
            onSelectTab={setSelectedTab}
            onSearchClick={openSearch}
          />
        )}

        {/* Page content */}
        <Box
          flex={{ base: 'none', md: '1' }}
          minH={{ base: 'auto', md: '0' }}
          px={isPathsRoute ? 0 : { base: '16px', md: '32px' }}
          pt={isPathsRoute ? 0 : { base: '8px', md: 0 }}
          pb={isPathsRoute ? 0 : { base: '24px', md: '16px' }}
          overflowX="hidden"
          overflowY={{ base: 'visible', md: isPathsRoute ? 'hidden' : 'auto' }}
        >
          <MotionBox
            key={location.pathname}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            h={{ base: 'auto', md: '100%' }}
            minH="100%"
          >
            {React.cloneElement(children as React.ReactElement, { selectedTab })}
          </MotionBox>
        </Box>
      </Flex>

      <GlobalSearch isOpen={searchOpen} onClose={closeSearch} />
      {hasHRChatAccess && <HRChatWidget />}
    </Flex>
  )
}

export default UserWrapper
